import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Crown } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLAG = { IN: "🇮🇳", US: "🇺🇸", GB: "🇬🇧", AE: "🇦🇪", SG: "🇸🇬" };

export function FoundersSpotlight() {
  const [founders, setFounders] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/founders/spotlight`).then((r) => setFounders(r.data.founders || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (founders.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % founders.length), 6000);
    return () => clearInterval(t);
  }, [founders]);

  if (founders.length === 0) return null;
  const f = founders[idx];

  return (
    <div className="relative overflow-hidden border-y border-[#C9A961]/25 bg-gradient-to-r from-[#0a0806] via-[#141008] to-[#0a0806]"
         data-testid="home-founders-spotlight">
      {/* Gold aurora */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute -top-24 left-1/3 w-[500px] h-[300px] rounded-full blur-[100px]"
             style={{ background: "radial-gradient(circle,#C9A961 0%,transparent 70%)" }} />
      </div>
      <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-14">
        <div className="flex items-center gap-3 mb-6">
          <motion.div animate={{ rotate: [0, 3, -3, 0] }} transition={{ duration: 6, repeat: Infinity }}>
            <Crown size={16} className="text-[#C9A961]" />
          </motion.div>
          <span className="font-grotesk text-[10px] uppercase tracking-[0.35em] text-[#C9A961]">
            · The Founders behind Vinci ·
          </span>
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={f.name + idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="grid md:grid-cols-[auto_1fr] gap-8 items-center"
          >
            {/* Portrait / monogram */}
            <div className="relative w-28 h-28 md:w-36 md:h-36 rounded-full border-2 border-[#C9A961]/50 grid place-items-center bg-[#0a0806] overflow-hidden"
                 style={{ boxShadow: "0 0 30px rgba(201,169,97,0.25)" }}>
              {f.has_photo && f.id ? (
                <img src={`${API}/founders/photo/${f.id}`} alt={f.name}
                  className="w-full h-full object-cover" />
              ) : (
                <span className="font-serif text-4xl md:text-5xl text-[#C9A961]">
                  {(f.name || "V").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </span>
              )}
              <span className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full grid place-items-center bg-[#0a0806] border border-[#C9A961]/60 text-xl">
                {FLAG[f.country] || "🏳️"}
              </span>
            </div>
            {/* Quote */}
            <div>
              <blockquote className="font-serif text-xl md:text-3xl text-[#F5F5F0] leading-snug italic">
                "{f.quote}"
              </blockquote>
              <div className="mt-4 font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">
                — {f.name} · {f.business_name}
                {f.state_or_region ? ` · ${f.state_or_region}` : ""}
              </div>
              <div className="mt-1 font-body text-xs text-[#8A8A82]">
                Founder · {f.shareholding_pct.toFixed(2)}% cap-table stake
                {f.since ? ` · Since ${f.since}` : ""}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
        {/* Progress dots */}
        {founders.length > 1 && (
          <div className="mt-8 flex gap-2 justify-center">
            {founders.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)}
                data-testid={`spotlight-dot-${i}`}
                className={`h-1 rounded-full transition-all ${i === idx ? "w-10 bg-[#C9A961]" : "w-4 bg-white/15 hover:bg-white/30"}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
